import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, Printer, X } from "lucide-react";
import type { Order } from "@/types/orders";

interface CardPaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: () => void;
  order: Order | null;
}

export default function CardPaymentSuccessModal({
  isOpen,
  onClose,
  onPrint,
  order,
}: CardPaymentSuccessModalProps) {
  if (!order) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader className="sr-only">
          <DialogTitle>Cobro con Tarjeta Realizado</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-6">
          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-green-600 mb-2">
              ¡Pago Aprobado!
            </h3>
            <p className="text-muted-foreground">
              El cobro con tarjeta se ha registrado correctamente
            </p>
          </div>

          {/* Order Details */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pedido:</span>
              <span className="font-semibold">#{order.order_number}</span>
            </div>
            {order.table_number && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Mesa:</span>
                <span className="font-semibold">{order.table_number}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Método:</span>
              <span className="font-semibold">💳 Tarjeta (Local)</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-border pt-2 mt-2">
              <span>Total Cobrado:</span>
              <span className="text-green-600">{order.total.toFixed(2)}€</span>
            </div>
          </div>

          {/* Info notice */}
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300 text-center">
              ℹ️ Cobro registrado. Utiliza tu terminal bancario físico para procesar la transacción.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={onPrint}
            className="h-14 text-lg font-bold bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Printer className="h-5 w-5 mr-2" />
            Imprimir
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="h-14 text-lg font-semibold border-2"
          >
            <X className="h-5 w-5 mr-2" />
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
